import { unwrap } from "../api/scoreApi";
import { classes, rides, tests, people, horses, breaks, setMeta } from "./db";

// #641 — the setup-time "prefetch the whole day" step
// (docs/score/announcer.md "Freshness" / "Degraded mode is a requirement").
// Pulls everything the Order of Go screen (#642+) needs for the selected
// show + ring into IndexedDB, so the app is fully usable the moment the venue
// wifi drops. Read-only, no outbox — unlike scribe-fe's sync engine this never
// pushes anything back.
//
//   classes(showId)        -> class headers + scheduledTime (derived, #623)
//   rides(showId, ring)     -> the ring's time-ordered running order (#637)
//   breaks(showId, ring)    -> the ring's scheduled breaks (#638)
//   tests(showId)           -> every test used by the show (per-ride resolve)
//   people() / horses()     -> org-wide (no per-show filter on these routes,
//                               same as scribe-fe's hydrate()); small enough
//                               to keep in full so a name/pedigree lookup
//                               never round-trips.
export async function prefetchDay(api, { showId, ring }) {
  if (!showId || !ring) return { classes: 0, rides: 0, tests: 0, people: 0, horses: 0, breaks: 0 };

  const [classList, rideList, breakList, peopleList, horseList] = await Promise.all([
    api.classes.listClasses(showId).then(unwrap).catch(() => []),
    api.rides.listRides(undefined, showId, undefined, ring).then(unwrap).catch(() => []),
    api.breaks.listBreaks(showId, ring).then(unwrap).catch(() => []),
    api.people.listPeople().then(unwrap).catch(() => []),
    api.horses.listHorses().then(unwrap).catch(() => []),
  ]);

  await classes.putAll(classList ?? []);
  await rides.putAll(rideList ?? []);
  await breaks.putAll(breakList ?? []);
  await people.putAll(peopleList ?? []);
  await horses.putAll(horseList ?? []);

  // #435 — test is a per-RIDE property (class.testId is only a hint), so
  // prefetch every test actually used by a ride in this ring, same rule
  // scribe-fe's hydrate() follows.
  const testIds = new Set((rideList ?? []).map((r) => r.testId).filter(Boolean));
  const testList = await Promise.all(
    [...testIds].map((id) => api.tests.getTest(id).then(unwrap).catch(() => null)),
  );
  await tests.putAll(testList.filter(Boolean));

  const counts = {
    classes: classList?.length ?? 0,
    rides: rideList?.length ?? 0,
    breaks: breakList?.length ?? 0,
    people: peopleList?.length ?? 0,
    horses: horseList?.length ?? 0,
    tests: testList.filter(Boolean).length,
  };
  await setMeta("lastPrefetchedAt", new Date().toISOString());
  return counts;
}
